/**
 * CoachPlaybook - Auth Context
 * Version: 0.6.0
 * Description: React context provider for Discord OAuth auth state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getStoredUser,
  getToken,
  getDiscordAuthUrl,
  logout as clientLogout,
  exchangeCode as clientExchangeCode,
  authFetch
} from './authClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tier, setTier] = useState('free');
  const [isLoading, setIsLoading] = useState(true);

  // Validate existing session on mount
  useEffect(() => {
    const validateSession = async () => {
      const storedUser = getStoredUser();
      const token = getToken();

      if (!storedUser || !token) {
        setIsLoading(false);
        return;
      }

      // Optimistically set user from localStorage
      setUser(storedUser);
      setTier(storedUser.tier || 'free');

      try {
        // Validate token against the API
        const res = await authFetch('/api/v1/auth/me');
        if (res.ok) {
          const freshUser = await res.json();
          setUser(freshUser);
          setTier(freshUser.tier || 'free');
        } else {
          // Token invalid, clear state
          clientLogout();
          setUser(null);
          setTier('free');
        }
      } catch {
        // Network error — keep optimistic user, they can work offline
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
  }, []);

  // Redirect to Discord OAuth
  const login = useCallback(() => {
    window.location.href = getDiscordAuthUrl();
  }, []);

  // Handle OAuth code exchange
  const exchangeCode = useCallback(async (code) => {
    const authUser = await clientExchangeCode(code);
    setUser(authUser);
    setTier(authUser.tier || 'free');
    return authUser;
  }, []);

  // Logout and clear state
  const logout = useCallback(() => {
    clientLogout();
    setUser(null);
    setTier('free');
  }, []);

  const value = {
    user,
    tier,
    isLoggedIn: !!user,
    isLoading,
    login,
    logout,
    exchangeCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
